import React, { useRef, useState } from 'react';
import Icon from '../AppIcon';
import { getAuthHeaders } from '../../lib/api';
import {
  compressImageForMms,
  MMS_SOURCE_MAX_BYTES,
} from '../../utils/compress-image-for-mms';

const ALLOWED_TYPES = new Set(['image/jpeg', 'image/png', 'image/gif', 'image/jpg']);

/**
 * Optional personal photo uploader for touchpoints / first-touch.
 * Soft checklist is non-blocking. Large photos are auto-compressed for MMS.
 *
 * @param {'touchpoint'|'first-touch'} purpose
 * @param {string} [touchpointId] required when purpose=touchpoint
 */
export default function TouchpointImageUploader({
  imageUrl,
  purpose = 'touchpoint',
  touchpointId,
  onSaved,
  alignHint = 'this message',
  disabled = false,
  title = 'Personal photo (optional)',
  description,
  showCameraOption = false,
}) {
  const inputRef = useRef(null);
  const cameraInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const [showChecklist, setShowChecklist] = useState(false);
  const [pendingFile, setPendingFile] = useState(null);
  const [checks, setChecks] = useState({ me: false, personal: false, matches: false });
  const [compressNote, setCompressNote] = useState(null);

  const helpText =
    description ||
    `Optional — a personal photo that matches ${alignHint} can be quite powerful. Use a photo of yourself that looks like you took it (not stock). Large photos are compressed automatically for MMS.`;

  const resetChecklist = () => {
    setShowChecklist(false);
    setPendingFile(null);
    setChecks({ me: false, personal: false, matches: false });
    setCompressNote(null);
    if (inputRef.current) inputRef.current.value = '';
    if (cameraInputRef.current) cameraInputRef.current.value = '';
  };

  const onFilePicked = (e) => {
    const file = e.target.files?.[0];
    setError(null);
    setCompressNote(null);
    if (!file) return;

    if (!ALLOWED_TYPES.has(file.type) && !/\.(jpe?g|png|gif)$/i.test(file.name)) {
      setError('Please use a JPEG, PNG, or GIF image.');
      return;
    }
    if (file.size > MMS_SOURCE_MAX_BYTES) {
      setError('That file is too large (max 25MB). Pick a smaller photo.');
      return;
    }

    if (file.size > 1024 * 1024) {
      setCompressNote(
        `Photo is ${(file.size / (1024 * 1024)).toFixed(1)}MB — we'll compress it for reliable MMS.`
      );
    }

    setPendingFile(file);
    setShowChecklist(true);
    setChecks({ me: false, personal: false, matches: false });
  };

  const blobToBase64 = (blob) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const result = String(reader.result || '');
        const base64 = result.includes(',') ? result.split(',')[1] : result;
        resolve(base64);
      };
      reader.onerror = () => reject(new Error('Failed to read file'));
      reader.readAsDataURL(blob);
    });

  const uploadFile = async (file) => {
    if (!file) {
      setError('No file selected.');
      return;
    }
    if (purpose === 'touchpoint' && !touchpointId) {
      setError('Missing touchpoint id.');
      return;
    }
    setUploading(true);
    setError(null);
    try {
      const compressed = await compressImageForMms(file);
      if (compressed.compressed) {
        setCompressNote(
          `Compressed ${(compressed.originalBytes / (1024 * 1024)).toFixed(1)}MB → ${(compressed.finalBytes / 1024).toFixed(0)}KB for MMS.`
        );
      }

      const dataBase64 = await blobToBase64(compressed.blob);
      const headers = await getAuthHeaders();
      const res = await fetch('/api/touchpoints/upload-image', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...headers },
        body: JSON.stringify({
          purpose,
          touchpointId: touchpointId || undefined,
          fileName: compressed.fileName,
          contentType: compressed.contentType,
          dataBase64,
        }),
      });
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.error || data.details || 'Upload failed');
      }
      await onSaved?.(data.url);
      resetChecklist();
    } catch (err) {
      console.error('Touchpoint image upload failed:', err);
      setError(err.message || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleConfirmUpload = async () => {
    if (!pendingFile) return;
    await uploadFile(pendingFile);
  };

  const handleRemove = async () => {
    setUploading(true);
    setError(null);
    try {
      if (purpose === 'touchpoint' && !touchpointId) {
        throw new Error('Missing touchpoint id.');
      }
      const headers = await getAuthHeaders();
      const res = await fetch('/api/touchpoints/upload-image', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...headers },
        body: JSON.stringify({
          purpose,
          touchpointId: touchpointId || undefined,
          clear: true,
        }),
      });
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.error || data.details || 'Failed to remove image');
      }
      await onSaved?.(null);
    } catch (err) {
      setError(err.message || 'Failed to remove image');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="rounded-lg border border-border p-3 space-y-2">
      <div className="flex items-start justify-between gap-2">
        <div>
          <p className="text-xs font-medium text-foreground">{title}</p>
          <p className="text-[11px] text-muted-foreground mt-0.5 leading-snug">
            {helpText}
          </p>
        </div>
        {imageUrl && (
          <img
            src={imageUrl}
            alt="Touchpoint attachment"
            className="w-14 h-14 rounded-md object-cover border border-border flex-shrink-0"
          />
        )}
      </div>

      <input
        ref={inputRef}
        type="file"
        accept="image/jpeg,image/png,image/gif,.jpg,.jpeg,.png,.gif"
        className="sr-only"
        disabled={disabled || uploading}
        onChange={onFilePicked}
      />
      {showCameraOption && (
        <input
          ref={cameraInputRef}
          type="file"
          accept="image/*"
          capture="user"
          className="sr-only"
          disabled={disabled || uploading}
          onChange={onFilePicked}
        />
      )}

      <div className="flex flex-wrap gap-2">
        {showCameraOption && (
          <button
            type="button"
            disabled={disabled || uploading}
            onClick={() => cameraInputRef.current?.click()}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border border-border hover:bg-muted transition-colors disabled:opacity-50"
          >
            <Icon name="Camera" size={13} />
            {imageUrl ? 'Retake selfie' : 'Take a selfie'}
          </button>
        )}
        <button
          type="button"
          disabled={disabled || uploading}
          onClick={() => inputRef.current?.click()}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border border-border hover:bg-muted transition-colors disabled:opacity-50"
        >
          <Icon name={imageUrl ? 'RefreshCw' : 'Image'} size={13} />
          {imageUrl ? (showCameraOption ? 'Choose different' : 'Replace') : 'Upload photo'}
        </button>
        {imageUrl && (
          <button
            type="button"
            disabled={disabled || uploading}
            onClick={handleRemove}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg text-destructive hover:bg-destructive/10 transition-colors disabled:opacity-50"
          >
            <Icon name="Trash2" size={13} />
            Remove
          </button>
        )}
      </div>

      {showChecklist && pendingFile && (
        <div className="bg-muted/40 border border-border rounded-lg p-3 space-y-2">
          <p className="text-xs font-medium text-foreground">Quick check (optional — you can skip)</p>
          {compressNote && (
            <p className="text-[11px] text-muted-foreground">{compressNote}</p>
          )}
          {[
            { key: 'me', label: 'This is me' },
            { key: 'personal', label: 'It looks like I took it (not a listing/stock shot)' },
            { key: 'matches', label: `It matches ${alignHint}` },
          ].map((item) => (
            <label key={item.key} className="flex items-start gap-2 text-xs text-foreground cursor-pointer">
              <input
                type="checkbox"
                className="mt-0.5 rounded border-border"
                checked={checks[item.key]}
                onChange={(e) => setChecks((prev) => ({ ...prev, [item.key]: e.target.checked }))}
              />
              <span>{item.label}</span>
            </label>
          ))}
          <div className="flex gap-2 pt-1">
            <button
              type="button"
              disabled={uploading}
              onClick={handleConfirmUpload}
              className="px-3 py-1.5 text-xs font-medium rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
            >
              {uploading ? 'Compressing & uploading…' : 'Upload photo'}
            </button>
            <button
              type="button"
              disabled={uploading}
              onClick={resetChecklist}
              className="px-3 py-1.5 text-xs font-medium rounded-lg text-muted-foreground hover:text-foreground"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {error && (
        <p className="text-xs text-destructive flex items-center gap-1">
          <Icon name="AlertCircle" size={12} />
          {error}
        </p>
      )}
    </div>
  );
}
